import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';

export const orderDocumentsQueryKeys = {
  all: ['orders', 'documents'] as const,
  order: (orderId: string) => [...orderDocumentsQueryKeys.all, 'order', orderId] as const,
  detail: (orderId: string, documentId: string) =>
    [...orderDocumentsQueryKeys.order(orderId), 'document', documentId] as const,
};

// Document type
export interface OrderDocument {
  id: string;
  transaction_id: string;
  file_name: string;
  document_type: 'cd' | 'le' | 'other';
  processing_status: 'pending' | 'processing' | 'validating' | 'completed' | 'failed';
  validation_session_id?: string;
  extraction_data?: unknown;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
}

// Response type for list documents
interface OrderDocumentsResponse {
  documents: OrderDocument[];
}

/**
 * Hook to fetch documents for an order
 * Polls when documents are in processing or validating status to get updates
 */
export function useOrderDocuments(orderId: string) {
  return useQuery({
    queryKey: orderDocumentsQueryKeys.order(orderId),
    queryFn: async () => {
      const response = await apiClient.get<OrderDocumentsResponse>(`/orders/${orderId}/documents`);
      return response.documents;
    },
    enabled: !!orderId,
    // Poll every 2 seconds if any document is in processing or validating status
    refetchInterval: (data) => {
      if (!data || !Array.isArray(data)) return false;
      const hasActiveDocument = data.some(
        (doc) => doc.processing_status === 'processing' || doc.processing_status === 'validating',
      );
      return hasActiveDocument ? 2000 : false;
    },
  });
}

/**
 * Hook to process a document manually
 */
export function useProcessDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId }: { documentId: string }) => {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}/process`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to process document');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate documents queries to trigger refetch
      queryClient.invalidateQueries({
        queryKey: orderDocumentsQueryKeys.all,
      });
    },
  });
}
